var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../context/ConfigContext';
import { useEventApi } from './useEvent/useEventApi';
import { useEventComposerGlobalContext } from '../composers/EventComposerGlobalContext';
import { transformSpaceAPIToSpaceType } from '../SpaceBooking/utils';
import { resolveSpacesAfterAdd } from '../utils/resolveSpacesAfterAdd';
/**
 * Fetches full space details for a space returned by the invitee search and
 * appends it to `form.spaces`. Uses the shared `fetchSpace` from `useEventApi`
 * so error handling is forwarded through the composer's `onError` channel.
 */
export var useAddSpaceFromSearch = function () {
    var _a = useConfig(), environmentUrls = _a.environmentUrls, userContext = _a.userContext, tenantId = _a.tenantId, accessToken = _a.accessToken;
    var _b = useEventComposerGlobalContext(), onError = _b.onError, setSpaces = _b.setSpaces, initialSpaces = _b.initialSpaces;
    var fetchSpace = useEventApi({
        onError: onError,
        apiUrl: environmentUrls.apiUrl,
        baseApiUrl: environmentUrls.baseApiUrl,
        currentOrg: userContext.currentOrg,
        tenantId: tenantId,
        accessToken: accessToken,
    }).fetchSpace;
    var getValues = useFormContext().getValues;
    var t = useTranslation('spaceBooking').t;
    var addSpaceById = useCallback(function (spaceId) { return __awaiter(void 0, void 0, void 0, function () {
        var currentSpaces, spaceApi, space, freshSpaces, nextSpaces, organizer;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    currentSpaces = (_a = getValues('spaces')) !== null && _a !== void 0 ? _a : [];
                    if (currentSpaces.some(function (s) { return s.id === spaceId; }))
                        return [2 /*return*/];
                    return [4 /*yield*/, fetchSpace({ spaceId: String(spaceId) })];
                case 1:
                    spaceApi = _d.sent();
                    if (!spaceApi)
                        return [2 /*return*/];
                    space = transformSpaceAPIToSpaceType(spaceApi);
                    freshSpaces = (_b = getValues('spaces')) !== null && _b !== void 0 ? _b : [];
                    if (freshSpaces.some(function (s) { return s.id === spaceId; }))
                        return [2 /*return*/];
                    nextSpaces = resolveSpacesAfterAdd(freshSpaces, space, initialSpaces);
                    if (nextSpaces === freshSpaces) {
                        organizer = initialSpaces === null || initialSpaces === void 0 ? void 0 : initialSpaces.find(function (s) { return s.isOrganizer; });
                        onError(new Error(t('request_only_blocked_by_organizer', {
                            name: (_c = organizer === null || organizer === void 0 ? void 0 : organizer.name) !== null && _c !== void 0 ? _c : '',
                        })));
                        return [2 /*return*/];
                    }
                    setSpaces(nextSpaces);
                    return [2 /*return*/];
            }
        });
    }); }, [fetchSpace, getValues, setSpaces, initialSpaces, onError, t]);
    // Monotonic token so a slow reconcile (awaiting space fetches) can't
    // clobber the result of a newer one that finished first.
    var reconcileToken = useRef(0);
    /**
     * Reconcile `form.spaces` against a full ordered id list (first is
     * primary): spaces already in the form are kept (and reordered), missing
     * ones are fetched, spaces absent from the list are dropped.
     *
     * A content-equal list is a strict no-op — this is load-bearing: the
     * shell composer's own outgoing echo carries the form's current
     * `spaceIds`, so without the no-op this would loop on its own echo.
     */
    var reconcileSpacesByIds = useCallback(function (spaceIds) { return __awaiter(void 0, void 0, void 0, function () {
        var targetIds, currentSpaces, sameList, token, resolved, nextSpaces;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    targetIds = spaceIds.map(Number).filter(function (id) { return !Number.isNaN(id); });
                    currentSpaces = (_a = getValues('spaces')) !== null && _a !== void 0 ? _a : [];
                    sameList = targetIds.length === currentSpaces.length &&
                        targetIds.every(function (id, index) { var _a; return ((_a = currentSpaces[index]) === null || _a === void 0 ? void 0 : _a.id) === id; });
                    if (sameList)
                        return [2 /*return*/];
                    token = ++reconcileToken.current;
                    return [4 /*yield*/, Promise.all(targetIds.map(function (id) { return __awaiter(void 0, void 0, void 0, function () {
                            var existing, spaceApi;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        existing = currentSpaces.find(function (s) { return s.id === id; });
                                        if (existing)
                                            return [2 /*return*/, existing];
                                        return [4 /*yield*/, fetchSpace({ spaceId: String(id) })];
                                    case 1:
                                        spaceApi = _a.sent();
                                        return [2 /*return*/, spaceApi ? transformSpaceAPIToSpaceType(spaceApi) : undefined];
                                }
                            });
                        }); }))];
                case 1:
                    resolved = _b.sent();
                    // A newer reconcile started while we were fetching — let it win.
                    if (token !== reconcileToken.current)
                        return [2 /*return*/];
                    nextSpaces = resolved.filter(function (space) { return Boolean(space); });
                    // Request-only exclusivity and identity rehydration are handled by
                    // setSpaces (normalizeSpaces) — no inline rule needed here.
                    setSpaces(nextSpaces);
                    return [2 /*return*/];
            }
        });
    }); }, [fetchSpace, getValues, setSpaces]);
    return { addSpaceById: addSpaceById, reconcileSpacesByIds: reconcileSpacesByIds };
};
